/**
 * Site-wide live formatting for Australian phone inputs (client-requested formats):
 *   mobiles   04XX XXX XXX
 *   landlines 0X XXXX XXXX
 *
 * Applies to every `input[type="tel"]` via document-level delegation — quote contact
 * step, NetSuite CRM forms, and the Shopify contact form all included, with no
 * per-form wiring:
 *   - while typing: progressive mask with caret preservation (deletions are left
 *     alone so backspacing over separators doesn't fight the user)
 *   - on commit (blur/change): full normalisation, including +61 → 0 conversion
 *
 * Non-AU-shaped values (1800/13 numbers, international, partial input) pass through
 * untouched. Submit-time formatting in the individual form components remains as the
 * data-level guarantee; this layer is presentational.
 */

/**
 * Full normalisation for a committed value.
 * @param {string} raw
 * @returns {string}
 */
export function formatAuPhone(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return trimmed;

  let digits = trimmed.replace(/\D+/g, '');
  if (digits.startsWith('61') && digits.length === 11) {
    digits = `0${digits.slice(2)}`;
  }
  // Missing leading zero (legacy-theme behaviour, with a length guard): a 9-digit
  // number that isn't 0-led is an AU number typed without its 0.
  if (digits.length === 9 && !digits.startsWith('0')) {
    digits = `0${digits}`;
  }

  if (digits.length === 10 && digits.startsWith('04')) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10 && digits.startsWith('0')) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }

  return trimmed;
}

/**
 * Progressive formatting while typing. Leaves international-looking ("+") values
 * and non-0-leading numbers alone until blur.
 * @param {string} raw
 * @returns {string}
 */
function formatAuPhonePartial(raw) {
  const value = String(raw || '');
  if (!value.trim() || value.includes('+')) return value;

  const digits = value.replace(/\D+/g, '');
  if (!digits.startsWith('0')) return value;

  const main = digits.slice(0, 10);
  const overflow = digits.slice(10);

  const groups = main.startsWith('04')
    ? [main.slice(0, 4), main.slice(4, 7), main.slice(7, 10)]
    : [main.slice(0, 2), main.slice(2, 6), main.slice(6, 10)];

  return groups.filter(Boolean).join(' ') + overflow;
}

/**
 * Reformat an input's value while keeping the caret anchored to the same digit.
 * @param {HTMLInputElement} input
 */
function maskWithCaret(input) {
  const before = input.value;
  const caret = input.selectionStart ?? before.length;
  const digitsBeforeCaret = before.slice(0, caret).replace(/\D+/g, '').length;

  const formatted = formatAuPhonePartial(before);
  if (formatted === before) return;

  input.value = formatted;

  let position = 0;
  let seen = 0;
  while (position < formatted.length && seen < digitsBeforeCaret) {
    if (/\d/.test(formatted[position])) seen += 1;
    position += 1;
  }
  try {
    input.setSelectionRange(position, position);
  } catch {
    // Some input states don't support selection — harmless.
  }
}

/** @param {EventTarget | null} target */
function asTelInput(target) {
  return target instanceof HTMLInputElement && target.type === 'tel' ? target : null;
}

document.addEventListener('input', (event) => {
  const input = asTelInput(event.target);
  if (!input) return;
  // Don't fight deletions — reformat resumes on the next insertion or blur.
  if (event instanceof InputEvent && event.inputType && event.inputType.startsWith('delete')) return;
  maskWithCaret(input);
});

document.addEventListener(
  'change',
  (event) => {
    const input = asTelInput(event.target);
    if (!input) return;
    const formatted = formatAuPhone(input.value);
    if (formatted !== input.value) input.value = formatted;
  },
  // Capture so the full format lands before component change-handlers save the value.
  true,
);
